import {
  IonContent,
  IonPage,
  IonHeader,
  IonToolbar,
  IonButtons,
  IonBackButton,
  IonTitle,
} from '@ionic/react';
import { Link } from 'react-router-dom';
import { usePageView } from '../hooks/useLoggerInit';
import { useI18n } from '../i18n/I18nProvider';

const PrivacyPage: React.FC = () => {
  // Track page view
  usePageView('privacy');
  const { t } = useI18n();

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref="/" text={t('common.actions.back')} />
          </IonButtons>
          <IonTitle>{t('legal.privacy.title')}</IonTitle>
        </IonToolbar>
      </IonHeader>
      
      <IonContent fullscreen className="ion-padding">
        <div className="legal-content">
          <h1>{t('legal.privacy.title')}</h1>
          <p className="last-updated">
            {t('legal.privacy.lastUpdated', { date: 'July 22, 2026' })}
          </p>

          <section>
            <h2>1. Introduction</h2>
            <p>
              Users Web App ("we", "our", or "us") is committed to protecting your privacy. 
              This Privacy Policy explains how we collect, use, disclose, and safeguard your 
              information when you use our mobile application and web service.
            </p>
          </section>

          <section>
            <h2>2. Information We Collect</h2>
            
            <h3>Personal Information</h3>
            <p>We may collect personal information that you provide directly, including:</p>
            <ul>
              <li>Name and email address</li>
              <li>Phone number</li>
              <li>Profile picture</li>
              <li>Account credentials</li>
            </ul>

            <h3>Usage Information</h3>
            <p>We automatically collect certain information when you use the App:</p>
            <ul>
              <li>Device information (type, operating system, unique identifiers)</li>
              <li>Log data (access times, pages viewed, app features used)</li>
              <li>Location data (with your consent)</li>
              <li>Activity logs for service improvement</li>
            </ul>
          </section>

          <section>
            <h2>3. How We Use Your Information</h2>
            <p>We use the collected information to:</p>
            <ul>
              <li>Provide and maintain the App</li>
              <li>Process your account registration</li>
              <li>Send you updates and notifications</li>
              <li>Respond to your inquiries and support requests</li>
              <li>Improve and personalize your experience</li>
              <li>Analyze usage patterns and trends</li>
              <li>Ensure security and prevent fraud</li>
            </ul>
          </section>

          <section>
            <h2>4. Information Sharing</h2>
            <p>
              We do not sell, trade, or rent your personal information to third parties. 
              We may share your information only in the following circumstances:
            </p>
            <ul>
              <li><strong>Service Providers:</strong> Third-party vendors who assist in operating the App</li>
              <li><strong>Legal Requirements:</strong> When required by law or to protect our rights</li>
              <li><strong>Business Transfers:</strong> In connection with a merger or acquisition</li>
              <li><strong>With Your Consent:</strong> When you explicitly agree to the sharing</li>
            </ul>
          </section>

          <section>
            <h2>5. Data Security</h2>
            <p>
              We implement appropriate technical and organizational security measures to protect 
              your personal information, including:
            </p>
            <ul>
              <li>Encryption of data in transit and at rest</li>
              <li>Secure authentication through Clerk</li>
              <li>Regular security assessments</li>
              <li>Access controls and monitoring</li>
            </ul>
          </section>

          <section>
            <h2>6. Your Rights</h2>
            <p>You have the right to:</p>
            <ul>
              <li>Access your personal information</li>
              <li>Correct inaccurate data</li>
              <li>Request deletion of your data</li>
              <li>Opt-out of marketing communications</li>
              <li>Export your data in a portable format</li>
            </ul>
            <p>
              {t('legal.privacy.dataRightsHowto')}{' '}
              <Link to="/settings/data">{t('privacyData.title')}</Link>
              {t('legal.privacy.dataRightsSignedIn')}
            </p>

            <h3>{t('legal.privacy.gdpr.title')}</h3>
            <p>{t('legal.privacy.gdpr.body')}</p>
            <ul>
              <li>{t('legal.privacy.gdpr.access')}</li>
              <li>{t('legal.privacy.gdpr.portability')}</li>
              <li>{t('legal.privacy.gdpr.erasure')}</li>
              <li>{t('legal.privacy.gdpr.rectification')}</li>
              <li>{t('legal.privacy.gdpr.restrict')}</li>
              <li>{t('legal.privacy.gdpr.complaint')}</li>
            </ul>
            <p>{t('legal.privacy.gdpr.lawfulBasis')}</p>
            <p>{t('legal.privacy.gdpr.retention')}</p>
            <p>{t('legal.privacy.gdpr.processors')}</p>
            <p>{t('legal.privacy.gdpr.contact')}</p>
          </section>

          <section>
            <h2>7. Cookies and Tracking</h2>
            <p>
              We use cookies and similar tracking technologies to enhance your experience. 
              You can control cookie preferences through your browser settings.
            </p>
          </section>

          <section>
            <h2>8. Children's Privacy</h2>
            <p>
              The App is not intended for children under 13 years of age. We do not knowingly 
              collect personal information from children under 13.
            </p>
          </section>

          <section>
            <h2>9. Changes to This Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. We will notify you of any 
              changes by posting the new policy on this page and updating the "Last updated" date.
            </p>
          </section>

          <section>
            <h2>10. Contact Us</h2>
            <p>
              If you have questions or concerns about this Privacy Policy, please contact us:
            </p>
            <p><strong>Email:</strong> privacy@users.web.app</p>
          </section>
        </div>

        <style>{`
          .legal-content {
            max-width: 800px;
            margin: 0 auto;
            padding-bottom: 48px;
          }

          .legal-content h1 {
            font-size: 28px;
            font-weight: 700;
            color: var(--ion-text-color);
            margin-bottom: 8px;
          }

          .legal-content .last-updated {
            color: var(--ion-color-medium);
            font-size: 14px;
            margin-bottom: 32px;
          }

          .legal-content section {
            margin-bottom: 32px;
          }

          .legal-content h2 {
            font-size: 20px;
            font-weight: 600;
            color: rgba(var(--ion-text-color-rgb), 0.92);
            margin-bottom: 12px;
          }

          .legal-content h3 {
            font-size: 17px;
            font-weight: 600;
            color: rgba(var(--ion-text-color-rgb), 0.9);
            margin-top: 16px;
            margin-bottom: 8px;
          }

          .legal-content p {
            font-size: 16px;
            line-height: 1.6;
            color: var(--ion-text-color);
            margin-bottom: 12px;
          }

          .legal-content ul {
            padding-left: 24px;
            margin-bottom: 12px;
          }

          .legal-content li {
            font-size: 16px;
            line-height: 1.6;
            color: var(--ion-text-color);
            margin-bottom: 8px;
          }
        `}</style>
      </IonContent>
    </IonPage>
  );
};

export default PrivacyPage;

